import { useCallback, useEffect, useState } from 'react';
import { Button, Input, List, Space, Tag, Modal, message, Switch, Avatar, Tooltip, Typography } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  SendOutlined,
  LockOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getComments,
  createComment,
  updateComment,
  deleteComment,
  type Comment,
} from '@/api/collaborationApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;
const { Text } = Typography;

interface CommentPanelProps {
  entityType: string;
  entityId: string;
}

export default function CommentPanel({ entityType, entityId }: CommentPanelProps) {
  const { t } = useTranslation();
  const { hasPermission, user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  const canRead = hasPermission('collaboration', 'read');
  const canWrite = hasPermission('collaboration', 'write');
  const canDelete = hasPermission('collaboration', 'delete');

  const loadComments = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const res = await getComments({
        entity_type: entityType,
        entity_id: entityId,
        from: 0,
        size: 200,
        sort_field: 'created_at',
        sort_descending: false,
      });
      if (res.data?.success && res.data.data) {
        setComments(res.data.data.items as Comment[]);
        setTotal(res.data.data.total);
      }
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, canRead, t]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      await createComment({
        entity_type: entityType,
        entity_id: entityId,
        content: content.trim(),
        is_internal: isInternal,
      });
      message.success(t('collaboration.comment.create_success'));
      setContent('');
      setIsInternal(false);
      loadComments();
    } catch {
      message.error(t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editContent.trim()) return;
    try {
      await updateComment(id, { content: editContent.trim() });
      message.success(t('collaboration.comment.update_success'));
      setEditingId(null);
      setEditContent('');
      loadComments();
    } catch {
      message.error(t('app.error'));
    }
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: t('app.confirm'),
      content: t('collaboration.comment.delete_confirm'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteComment(id);
          message.success(t('collaboration.comment.delete_success'));
          loadComments();
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  const startEdit = (comment: Comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  return (
    <div>
      <Text strong style={{ fontSize: 16, marginBottom: 16, display: 'block' }}>
        {t('collaboration.comments_title')} ({total})
      </Text>

      <List
        loading={loading}
        dataSource={comments}
        locale={{ emptyText: t('collaboration.comment.no_comments') }}
        renderItem={(item: Comment) => (
          <List.Item
            actions={[
              ...(canWrite && item.author_id === user?.user_id
                ? [
                    <Tooltip key="edit" title={t('collaboration.comment.edit')}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => startEdit(item)}
                      />
                    </Tooltip>,
                  ]
                : []),
              ...(canDelete && item.author_id === user?.user_id
                ? [
                    <Tooltip key="delete" title={t('collaboration.comment.delete')}>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(item.id)}
                      />
                    </Tooltip>,
                  ]
                : []),
            ]}
          >
            <List.Item.Meta
              avatar={<Avatar icon={<UserOutlined />} />}
              title={
                <Space>
                  <Text strong>{item.author_id}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(item.created_at).toLocaleString()}
                  </Text>
                  {item.is_internal && (
                    <Tag icon={<LockOutlined />} color="orange">
                      {t('collaboration.comment.field.is_internal')}
                    </Tag>
                  )}
                </Space>
              }
              description={
                editingId === item.id ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <TextArea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={2}
                    />
                    <Space>
                      <Button size="small" type="primary" onClick={() => handleUpdate(item.id)}>
                        {t('app.save')}
                      </Button>
                      <Button size="small" onClick={() => setEditingId(null)}>
                        {t('app.cancel')}
                      </Button>
                    </Space>
                  </Space>
                ) : (
                  <Text>{item.content}</Text>
                )
              }
            />
          </List.Item>
        )}
      />

      {canWrite && (
        <div style={{ marginTop: 16 }}>
          <TextArea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            placeholder={t('collaboration.comment.placeholder')}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={submitting}
              onClick={handleSubmit}
              disabled={!content.trim()}
            >
              {t('collaboration.comment.add')}
            </Button>
            <Space>
              <Switch
                checked={isInternal}
                onChange={setIsInternal}
                size="small"
              />
              <Text type="secondary">{t('collaboration.comment.field.is_internal')}</Text>
            </Space>
          </Space>
        </div>
      )}
    </div>
  );
}
